/**
 * Edit Device Form Composer
 *
 * Form for updating existing devices with cascading dropdowns.
 * - Manufacturer selection filters available device types
 * - deviceNumber is provided as parameter
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  EditDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderRadioControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import {
  filterDeviceTypesByManufacturer,
  findItemById,
} from '@/lib/dataTransformationUtils'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue } from '@/lib/utils'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Cpu } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type EditDeviceComposerProps = {
  deviceNumber: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues: Partial<EditDeviceFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof EditDeviceFormData, value: unknown): string | null => {
  const shape = EditDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function EditDeviceComposer(props: EditDeviceComposerProps) {
  const { deviceNumber, onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const { toast } = useToast()

  // Track selected manufacturer for filtering device types
  const [selectedManufacturer, setSelectedManufacturer] = React.useState<number | undefined>(
    initialValues?.manufacturerNumber
  )

  // When the user picks a device type we auto-sync the manufacturer. That manufacturer
  // change must NOT clear the device type the user just selected, so we flag the sync.
  const isSyncingManufacturerFromDeviceType = React.useRef(false)

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)

  // Only deviceStatus is required by the backend (upsertDevice: device.required = [deviceStatus]).
  // deviceName and deviceTypeNumber are optional, so they must not block editing.
  const isRequiredField = (name: string) => {
    // Backend requires only deviceStatus (deviceName/deviceTypeNumber are optional),
    // so nothing else must block editing.
    return ['deviceStatus'].includes(name)
  }

  // Fetch all select options with a single hook call
  const { optionsMap, loadingMap, rawData } = useSelectOptionsMap({
    manufacturerNumber: {
      endpoint: 'getDeviceManufacturers',
      dataKey: 'manufacturers',
      valueField: 'manufacturerNumber',
      labelField: 'manufacturerName',
    },
    deviceTypeNumber: {
      endpoint: 'getDeviceTypes',
      dataKey: 'deviceTypes',
      valueField: 'deviceTypeNumber',
      labelField: 'deviceTypeName',
      // Use dependent transformer for cascading dropdown (overrides valueField/labelField)
      dependentTransformer: (data, deps) =>
        filterDeviceTypesByManufacturer(data, deps?.selectedManufacturer),
    },
    distributorNumber: {
      endpoint: 'getDeviceDistributors',
      dataKey: 'distributors',
      valueField: 'distributorNumber',
      labelField: 'distributorName',
    },
    deviceProfileNumber: {
      endpoint: 'getDeviceProfiles',
      dataKey: 'deviceProfiles',
      valueField: 'deviceProfileNumber',
      labelField: 'deviceProfileName',
    },
  }, { selectedManufacturer })

  // Get raw device types data for cascading logic
  const deviceTypesData = rawData.deviceTypeNumber || []

  const updateDevice = useApiMutation('upsertDevice', 'POST', {
    // getDeviceByNumber is the single-device endpoint the detail page reads.
    // 'getDevice' matches nothing, so the detail would show stale data after edit.
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to update device:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditDeviceFormSchema,
    defaultValues: {
      deviceNumber: deviceNumber,
      deviceName: initialValues?.deviceName ?? '',
      manufacturerNumber: initialValues?.manufacturerNumber ?? undefined,
      deviceTypeNumber: initialValues?.deviceTypeNumber ?? ('' as any),
      deviceProfileNumber: initialValues?.deviceProfileNumber ?? undefined,
      distributorNumber: initialValues?.distributorNumber ?? undefined,
      // A device has one order source: default to distributor if it has one, else manufacturer.
      orderedFrom: initialValues?.distributorNumber ? 'distributor' : 'manufacturer',
      // Carry the device's real status (backend accepts all 13); fall back only if absent.
      deviceStatus: initialValues?.deviceStatus ?? 'ordered',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditDeviceFormData
      // Coerce and validate with Zod schema (single source of truth)
      const coerced = {
        deviceNumber: deviceNumber,
        // Omit an empty name instead of sending '' (backend rejects deviceName "").
        deviceName: formValue.deviceName?.trim() || undefined,
        manufacturerNumber: formValue.manufacturerNumber ? Number(formValue.manufacturerNumber) : undefined,
        deviceTypeNumber: formValue.deviceTypeNumber ? Number(formValue.deviceTypeNumber) : undefined,
        deviceProfileNumber: formValue.deviceProfileNumber ? Number(formValue.deviceProfileNumber) : undefined,
        distributorNumber: formValue.distributorNumber ? Number(formValue.distributorNumber) : undefined,
        deviceStatus: formValue.deviceStatus || undefined,
      }

      const parsed = EditDeviceFormSchema.safeParse(coerced)

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof EditDeviceFormData, (meta) => ({
              ...meta,
              isTouched: true,
              errors: [issue.message],
            }))
          }
        }
        // Surface the failure instead of silently doing nothing when the user clicks Update.
        const firstIssue = parsed.error.issues[0]
        toast({
          title: 'Cannot save device',
          description: firstIssue
            ? `${String(firstIssue.path[0] ?? 'Form')}: ${firstIssue.message}`
            : 'Please fix the highlighted fields.',
          type: 'error',
        })
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await updateDevice.mutateAsync(apiRequest)
    },
  })

  // Subscribe to orderedFrom to show only the one relevant source field.
  const orderedFrom = form.useStore((s: any) => s.values.orderedFrom as 'manufacturer' | 'distributor' | undefined)

  // Edit mode prefills every field, so reveal validation immediately: mark the
  // fields touched and validate once on mount. Any invalid/empty required field
  // then shows its error right away, instead of a silently-disabled Save button.
  React.useEffect(() => {
    const names: (keyof EditDeviceFormData)[] = [
      'deviceName',
      'deviceTypeNumber',
      'deviceStatus',
      'deviceProfileNumber',
      'distributorNumber',
      'manufacturerNumber',
    ]
    names.forEach((name) =>
      form.setFieldMeta(name, (meta) => ({ ...meta, isTouched: true })),
    )
    void form.validateAllFields('change')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle manufacturer change to filter device types (cascading dropdown)
  const handleManufacturerChange = (value: string) => {
    const numValue = value ? Number(value) : undefined
    setSelectedManufacturer(numValue)

    // This manufacturer change was triggered by the user selecting a device type —
    // keep that selection, don't clear it.
    if (isSyncingManufacturerFromDeviceType.current) {
      isSyncingManufacturerFromDeviceType.current = false
      return
    }

    // Keep the current device type if it belongs to the newly selected manufacturer
    // (coerce both sides — the API may return manufacturer numbers as strings).
    const currentDeviceTypeVal = form.getFieldValue('deviceTypeNumber')
    if (currentDeviceTypeVal) {
      const deviceType = findItemById(deviceTypesData, 'deviceTypeNumber', Number(currentDeviceTypeVal)) as any
      if (deviceType && Number(deviceType.deviceTypeManufacturerNumber) === numValue) {
        return
      }
    }

    // Clear device type selection when manufacturer changes
    form.setFieldValue('deviceTypeNumber', '' as any)
  }

  // Handle device type change (auto-update manufacturer)
  const handleDeviceTypeChange = (value: string) => {
    // If user selects a device type, update manufacturer dropdown to match
    if (value && deviceTypesData.length > 0) {
      const selectedType = findItemById(
        deviceTypesData,
        'deviceTypeNumber',
        Number(value)
      ) as any
      if (selectedType?.deviceTypeManufacturerNumber) {
        // Flag so the resulting manufacturer change doesn't wipe this selection.
        isSyncingManufacturerFromDeviceType.current = true
        setSelectedManufacturer(Number(selectedType.deviceTypeManufacturerNumber))
        form.setFieldValue('manufacturerNumber', String(selectedType.deviceTypeManufacturerNumber) as any)
      }
    }
  }

  // Custom handlers for specific fields
  const customHandlers: Record<string, (value: string) => void> = {
    manufacturerNumber: handleManufacturerChange,
    deviceTypeNumber: handleDeviceTypeChange,
  }

  // Render a single text control
  const renderTextControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          // Validate as the user types AND on blur, so each box shows its
          // own error immediately instead of the Save button silently disabling.
          onChange: ({ value }) =>
            validateField(
              control.name as keyof EditDeviceFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof EditDeviceFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render a single select control with visibility and disabled states
  const renderSelectControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)
    const isLoading = loadingMap[control.name]

    // Get options from API or YAML
    const options = control.optionsEndpoint
      ? optionsMap[control.name] || []
      : FormConfigLoader.normalizeSelectOptions(control.selectOptions)

    const descriptionText: string =
      control.name === 'deviceTypeNumber' && selectedManufacturer
        ? `Showing device types from selected manufacturer (${options.length} available)`
        : control.name === 'deviceTypeNumber'
        ? 'All device types shown. Select a manufacturer to filter.'
        : control.description ?? ''

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          // Validate on change AND on blur so the selected/required value is
          // checked live and its error is shown next to the field.
          onChange: ({ value }) =>
            validateField(
              control.name as keyof EditDeviceFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof EditDeviceFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
        listeners={customHandlers[control.name] ? {
          onChange: ({ value }) => customHandlers[control.name]?.(String(value ?? '')),
        } : undefined}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            description: descriptionText,
            options: options,
            isLoading: isLoading,
          }
          return renderSelectControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render the "Ordered From" radio; switching clears the other source field.
  const renderRadioControlField = (control: any) => {
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        listeners={control.name === 'orderedFrom' ? {
          onChange: ({ value }) => {
            const next = value as 'manufacturer' | 'distributor'
            if (next === 'manufacturer') {
              // Clear the distributor when switching to a manufacturer order.
              form.setFieldValue('distributorNumber', undefined as any)
            } else {
              // Clear the manufacturer filter (device type stays — it's the real link).
              form.setFieldValue('manufacturerNumber', undefined as any)
              setSelectedManufacturer(undefined)
            }
          },
        } : undefined}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            required: isRequiredField(control.name),
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            options: control.selectOptions || [],
          }
          return renderRadioControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Manufacturer dropdown (shown when orderedFrom === 'manufacturer'); filters device types.
  const renderManufacturerField = () => (
    <form.AppField
      key="manufacturerNumber"
      name="manufacturerNumber"
      validators={{
        onBlur: ({ value }) => validateField('manufacturerNumber', coerceFieldValue('manufacturerNumber', value)) || undefined,
      }}
      listeners={{ onChange: ({ value }) => handleManufacturerChange(String(value ?? '')) }}
    >
      {(bound) => renderSelectControl({
        name: 'manufacturerNumber',
        label: 'Manufacturer',
        placeholder: 'Select manufacturer',
        // UI-only — manufacturer isn't part of the upsertDevice payload.
        required: false,
        visibility: true,
        disabled: false,
        options: optionsMap['manufacturerNumber'] || [],
        isLoading: loadingMap['manufacturerNumber'],
      }, bound)}
    </form.AppField>
  )

  // Distributor dropdown (shown when orderedFrom === 'distributor').
  const renderDistributorField = () => (
    <form.AppField
      key="distributorNumber"
      name="distributorNumber"
      validators={{
        onBlur: ({ value }) => validateField('distributorNumber', coerceFieldValue('distributorNumber', value)) || undefined,
      }}
    >
      {(bound) => renderSelectControl({
        name: 'distributorNumber',
        label: 'Distributor',
        placeholder: 'Select distributor',
        required: false,
        visibility: true,
        disabled: false,
        options: optionsMap['distributorNumber'] || [],
        isLoading: loadingMap['distributorNumber'],
      }, bound)}
    </form.AppField>
  )

  // Universal render function that dispatches to specific renderers
  const renderControl = (control: any) => {
    if (control.htmlControl === 'radio') {
      return renderRadioControlField(control)
    }
    if (control.htmlControl === 'select') {
      return renderSelectControlField(control)
    }
    return renderTextControlField(control)
  }

  // Render form content. A device has ONE order source, so we render:
  // basic info (name, status) → Device Type → Ordered From + the single source dropdown.
  // manufacturerNumber/distributorNumber are rendered conditionally, never both.
  const renderFormContent = () => {
    const basicControls = controlsByGroup.get('basicInformationGroup') || []
    const classificationControls = controlsByGroup.get('classificationGroup') || []

    const orderedFromControl = basicControls.find((c) => c.name === 'orderedFrom')
    const otherBasicControls = basicControls.filter((c) => c.name !== 'orderedFrom')
    // Device Type is always shown; the manufacturer dropdown is rendered conditionally below.
    const deviceTypeControl = classificationControls.find((c) => c.name === 'deviceTypeNumber')
    // Device Profile (Feature 6) — optional link, shown alongside Device Type.
    const deviceProfileControl = classificationControls.find((c) => c.name === 'deviceProfileNumber')

    return (
      <>
        {/* 1. Basic info: name, status */}
        <div className="space-y-4">
          {otherBasicControls.map(renderControl)}
        </div>

        {/* 2. Device Type (+ optional Device Profile) — always shown */}
        <div className="space-y-4">
          {deviceTypeControl && renderControl(deviceTypeControl)}
          {deviceProfileControl && renderControl(deviceProfileControl)}
        </div>

        {/* 3. Ordered From + the single source field (manufacturer XOR distributor) */}
        <div className="space-y-4">
          {orderedFromControl && renderControl(orderedFromControl)}
          {orderedFrom === 'manufacturer' && renderManufacturerField()}
          {orderedFrom === 'distributor' && renderDistributorField()}
        </div>
      </>
    )
  }

  const formBody = (
    <>
        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: updateDevice.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Updating...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Update'),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Cpu className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  EditDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditDeviceFormData,
} from './schema'
